import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Loader2, ImagePlus, Trash2, Link2, Upload, Image as ImageIcon,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const MAX_GALLERY = 6;

/** Reusable Image Manager — primary upload + gallery + URL.
 *  Used by the Product Publishing dashboard's inline edit pencil. */
export default function ProductImageManagerDialog({
  open, onOpenChange, productId, productName, initialImage, initialGallery,
  onUpdated,
}) {
  const [primary, setPrimary] = useState(initialImage || '');
  const [gallery, setGallery] = useState(initialGallery || []);
  const [urlText, setUrlText] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setPrimary(initialImage || '');
      setGallery(initialGallery || []);
      setUrlText('');
    }
  }, [open, initialImage, initialGallery]);

  const onPickPrimary = () => fileInputRef.current?.click();
  const onPickGallery = () => galleryInputRef.current?.click();

  const uploadPrimary = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await axios.post(
        `${API}/admin/products/${productId}/image`, form,
        { headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' } },
      );
      setPrimary(data.product_image || '');
      onUpdated?.(data);
      toast.success('Primary image updated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const uploadGallery = async (files) => {
    if (!files?.length) return;
    if (gallery.length + files.length > MAX_GALLERY) {
      toast.error(`Gallery limit is ${MAX_GALLERY} images`);
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      Array.from(files).forEach((f) => form.append('files', f));
      const { data } = await axios.post(
        `${API}/admin/products/${productId}/images`, form,
        { headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' } },
      );
      setGallery(data.product_images || []);
      onUpdated?.(data);
      toast.success(`${files.length} image(s) appended`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const removeGalleryImg = async (imgId) => {
    setUploading(true);
    try {
      const { data } = await axios.delete(
        `${API}/admin/products/${productId}/images/${imgId}`,
        { headers: authHeaders() },
      );
      setGallery(data.product_images || []);
      onUpdated?.(data);
      toast.success('Image removed');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Remove failed');
    } finally {
      setUploading(false);
    }
  };

  const setFromUrl = async () => {
    const url = urlText.trim();
    if (!url) return;
    setUploading(true);
    try {
      const { data } = await axios.patch(
        `${API}/admin/products/${productId}/image-from-url`, { url },
        { headers: authHeaders() },
      );
      setPrimary(data.product_image || '');
      setUrlText('');
      onUpdated?.(data);
      toast.success('Primary image set from URL');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Set URL failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="product-image-manager-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ImageIcon className="w-4 h-4 text-orange-600" />
            Images — {productName || productId}
          </DialogTitle>
        </DialogHeader>

        {/* PRIMARY */}
        <div className="border rounded-lg p-3 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-600">
              Primary image
            </h3>
            <Badge variant="outline" className="text-[10px]">Used on OG App tile</Badge>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-32 h-32 border rounded bg-slate-50 flex items-center justify-center overflow-hidden flex-shrink-0">
              {primary ? (
                <img src={primary} alt="" className="w-full h-full object-contain"
                  data-testid="product-image-primary-preview" />
              ) : (
                <ImageIcon className="w-10 h-10 text-slate-300" />
              )}
            </div>
            <div className="flex-1 space-y-2">
              <div className="flex gap-2 flex-wrap">
                <Button size="sm" disabled={uploading} onClick={onPickPrimary}
                  data-testid="product-image-upload-btn">
                  <Upload className="w-4 h-4 mr-1" /> Upload File
                </Button>
                <input ref={fileInputRef} type="file" accept="image/*"
                  onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; uploadPrimary(f); }}
                  className="hidden" data-testid="product-image-file-input" />
              </div>
              <div className="flex items-center gap-2">
                <Link2 className="w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Paste image URL (https://...)" value={urlText}
                  onChange={(e) => setUrlText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') setFromUrl(); }}
                  className="h-8 text-sm"
                  data-testid="product-image-url-input"
                />
                <Button size="sm" variant="outline" disabled={uploading || !urlText.trim()}
                  onClick={setFromUrl} data-testid="product-image-url-submit">
                  Set
                </Button>
              </div>
              <p className="text-[11px] text-slate-400">
                JPEG / PNG / WEBP. Max 5 MB. Compressed to ≤ 200 KB WEBP and stored as base64 in the database (survives every deploy).
              </p>
            </div>
          </div>
        </div>

        {/* GALLERY */}
        <div className="border rounded-lg p-3 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-600">
              Gallery ({gallery.length} / {MAX_GALLERY})
            </h3>
            <Button size="sm" variant="outline" disabled={uploading || gallery.length >= MAX_GALLERY}
              onClick={onPickGallery}
              data-testid="product-image-gallery-add-btn">
              <ImagePlus className="w-4 h-4 mr-1" /> Add image{gallery.length < MAX_GALLERY - 1 ? 's' : ''}
            </Button>
            <input ref={galleryInputRef} type="file" accept="image/*" multiple
              onChange={(e) => { const f = e.target.files; e.target.value = ''; uploadGallery(f); }}
              className="hidden" data-testid="product-image-gallery-input" />
          </div>
          {gallery.length === 0 ? (
            <p className="text-xs text-slate-400">No additional images. The primary image is enough — gallery is optional.</p>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
              {gallery.map((g) => (
                <div key={g.id} className="relative group border rounded overflow-hidden"
                  data-testid={`product-image-gallery-item-${g.id}`}>
                  <img src={g.url} alt={g.alt || ''} className="w-full h-20 object-cover" />
                  <button
                    onClick={() => removeGalleryImg(g.id)}
                    className="absolute top-1 right-1 bg-rose-600 hover:bg-rose-700 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition"
                    data-testid={`product-image-gallery-remove-${g.id}`}>
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button onClick={() => onOpenChange(false)} disabled={uploading}
            data-testid="product-image-manager-close">
            {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
